import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';
import useCompanyProfileCompletionState from './completion.state';

export interface IKeyActivity {
  id: number | string;
  tenant_id?: number | string;
  org_chart_node_id?: number;
  org_chart_node?: {
    id: number;
    name: string;
  };
  name?: string;
  is_published?: boolean;
  published?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface IKeyActivityPaginatedResponse {
  content: IKeyActivity[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

interface KeyActivityState {
  keyActivities: IKeyActivity[];
  paginatedData: IKeyActivityPaginatedResponse | null;
  selectedKeyActivity: IKeyActivity | null;
  isLoading: boolean;
  error: unknown;

  createKeyActivity: (tenantId: string | number, payload: Partial<IKeyActivity>) => Promise<void>;
  getKeyActivities: (
    tenantId: string | number,
    params?: {
      page?: number;
      page_size?: number;
      org_chart_node_id?: number;
      name?: string;
      published?: boolean;
      orderBy?: string;
      order?: string;
    },
  ) => Promise<IKeyActivityPaginatedResponse | void>;
  getKeyActivityDetail: (tenantId: string | number, id: number | string) => Promise<IKeyActivity | void>;
  updateKeyActivity: (tenantId: string | number, id: number | string, payload: Partial<IKeyActivity>) => Promise<void>;
  deleteKeyActivity: (tenantId: string | number, id: number | string) => Promise<void>;
  bulkPublishKeyActivities: (tenantId: string | number, isPublished: boolean) => Promise<void>;
  clearKeyActivities: () => void;
  setKeyActivities: (activities: IKeyActivity[]) => void;
}

const useKeyActivityState = create<KeyActivityState>((set, get) => ({
  keyActivities: [],
  paginatedData: null,
  selectedKeyActivity: null,
  isLoading: false,
  error: null,
  setKeyActivities: activities => set({ keyActivities: activities }),
  clearKeyActivities: () => set({ keyActivities: [], paginatedData: null }),

  createKeyActivity: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}`;
      await axiosInstance.post(url, payload);

      await get().getKeyActivities(tenantId);
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();

      toast.success('Key Activity created successfully');
      set({ isLoading: false });
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  getKeyActivities: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}`;
      const { data } = await axiosInstance.get(url, { params });

      const paginatedResponse: IKeyActivityPaginatedResponse = {
        content: data.data?.content || [],
        totalElements: data.data?.totalElements || 0,
        totalPages: data.data?.totalPages || 0,
        size: data.data?.size || 10,
        number: data.data?.number || 0,
      };

      set({
        keyActivities: data.data?.content || [],
        paginatedData: paginatedResponse,
        isLoading: false,
      });

      return paginatedResponse;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  getKeyActivityDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}/${id}`;
      const { data } = await axiosInstance.get(url);
      set({ selectedKeyActivity: data.data, isLoading: false });
      return data.data;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  updateKeyActivity: async (tenantId, id, payload) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}/${id}`;
      const { data } = await axiosInstance.put(url, payload);
      set(state => ({
        keyActivities: state.keyActivities.map(ka => (ka.id === id ? { ...ka, ...data.data } : ka)),
        selectedKeyActivity: data.data,
        isLoading: false,
      }));
      toast.success('Key Activity updated successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  deleteKeyActivity: async (tenantId, id) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}/${id}`;
      await axiosInstance.delete(url);
      set(state => ({
        keyActivities: state.keyActivities.filter(ka => ka.id !== id),
        isLoading: false,
      }));
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      toast.success('Key Activity deleted successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  bulkPublishKeyActivities: async (tenantId: string | number, isPublished: boolean) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}/bulk-publish`;
      await axiosInstance.put(url, { published: isPublished });

      set(state => ({
        keyActivities: state.keyActivities.map(ka => ({
          ...ka,
          published: isPublished,
        })),
        isLoading: false,
      }));

      toast.success(
        isPublished ? 'All Key Activities published successfully' : 'All Key Activities unpublished successfully',
      );
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },
}));

export default useKeyActivityState;
